import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PackageSearch } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { partsWithoutType } from "@/lib/sparePartDonor";

type Row = {
  id: string;
  serial_no: string;
  name: string;
  company_id: string;
  part_category: string | null;
  brand: string | null;
  model_number: string | null;
  appliance_id: string | null;
  harvested_at: string;
  part_type_id: string | null;
  donor_legacy_missing: boolean;
};

/** Platform › Data › Parts without donor or part type (superadmin, read-only). FIX-286. */
export default function LegacyParts() {
  const { t } = useTranslation("admin");
  const [company, setCompany] = useState("all");

  const { data: companies = [] } = useQuery({
    queryKey: ["legacy_parts_companies"],
    queryFn: async () => {
      const { data, error } = await supabase.from("companies").select("id, name").order("name");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["legacy_parts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("spare_parts")
        .select("id, serial_no, name, company_id, part_category, brand, model_number, appliance_id, harvested_at, part_type_id, donor_legacy_missing")
        .or("donor_legacy_missing.eq.true,part_type_id.is.null")
        .order("harvested_at", { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data || []) as Row[];
    },
  });

  const companyName = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies]);
  const scoped = rows.filter((r) => company === "all" || r.company_id === company);
  const legacy = scoped.filter((r) => r.donor_legacy_missing);
  const noType = partsWithoutType(scoped);

  const table = (list: Row[], legacyCols: boolean) => (
    <div className="bg-card rounded-xl border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground">
          <tr className="border-b border-border">
            {legacyCols && <th className="p-3">{t("legacyParts.serial")}</th>}
            <th className="p-3">{t("legacyParts.name")}</th>
            <th className="p-3">{t("legacyParts.company")}</th>
            <th className="p-3">{t("legacyParts.partCategory")}</th>
            <th className="p-3">{t("legacyParts.brandModel")}</th>
            {legacyCols && <th className="p-3">{t("legacyParts.appliance")}</th>}
            {legacyCols && <th className="p-3">{t("legacyParts.harvested")}</th>}
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.id} className="border-b border-border last:border-0">
              {legacyCols && <td className="p-3 font-mono text-xs">{r.serial_no}</td>}
              <td className="p-3">{r.name}</td>
              <td className="p-3">{companyName.get(r.company_id) ?? "—"}</td>
              <td className="p-3">{r.part_category || "—"}</td>
              <td className="p-3">{[r.brand, r.model_number].filter(Boolean).join(" ") || "—"}</td>
              {legacyCols && <td className="p-3">{r.appliance_id ? t("legacyParts.yes") : t("legacyParts.no")}</td>}
              {legacyCols && <td className="p-3">{new Date(r.harvested_at).toLocaleDateString()}</td>}
            </tr>
          ))}
          {list.length === 0 && (
            <tr><td className="p-6 text-center text-muted-foreground" colSpan={7}>{isLoading ? "…" : t("legacyParts.empty")}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <PackageSearch className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("legacyParts.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("legacyParts.description")}</p>
      </div>
      <div className="px-6 max-w-[1800px] space-y-4">
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("legacyParts.allCompanies")}</SelectItem>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Tabs defaultValue="legacy">
          <TabsList>
            <TabsTrigger value="legacy">{t("legacyParts.tabLegacy")} ({legacy.length})</TabsTrigger>
            <TabsTrigger value="noType">{t("legacyParts.tabNoType")} ({noType.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="legacy">{table(legacy, true)}</TabsContent>
          <TabsContent value="noType">{table(noType, false)}</TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
